import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ParticipantGroupPriceTable.css';
import './Price.css';
import '../list/List.css';
import ListHeader from '../list/ListHeader';
import ListTextItem from '../list/ListTextItem';
import ActionButton from '../input/ActionButton';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import OfflineBanner from '../shell/OfflineBanner';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import { formatEuro } from '../../util/payment';
import { isAdmin } from '../../util/auth';
import { ROUTES } from '../../util/routes';

const COLUMNS = '2fr 1fr';

// Default price of a group; 0 reads as "kostenlos" (same wording as the WP price line).
function PriceCell({ value }) {
  const amount = Number(value ?? 0);
  return amount > 0
    ? <ListTextItem type={1}>{formatEuro(amount)}</ListTextItem>
    : <ListTextItem type={2}>kostenlos</ListTextItem>;
}

/**
 * Read-only table "Eintritt je Gruppe" on the price page (decision 9): the default entry prices of the
 * active groups. They are edited on /groups (admin); every event materializes its own copy, so single
 * events may differ. Add-on prices (formerly the "Leihe" column) are in the catalog below
 * (add-on/AddOnCatalog). The list comes from the shared group hook (offline: reference cache).
 */
function ParticipantGroupPriceTable() {
  const { active, loaded, failed } = useParticipantGroups();
  const navigate = useNavigate();
  const admin = isAdmin();

  let content;
  if (!loaded) {
    content = failed
      ? <p className="groupPriceMessage">Gruppen konnten nicht geladen werden.</p>
      : <p className="groupPriceMessage">Gruppen werden geladen …</p>;
  } else if (active.length === 0) {
    content = <p className="groupPriceMessage">Es sind keine aktiven Gruppen angelegt.</p>;
  } else {
    content = (
      <div className="groupPriceTable">
        <ListHeader gridTemplateColumns={COLUMNS}>
          <ListTextItem isHeader>Gruppe</ListTextItem>
          <ListTextItem isHeader>Eintritt</ListTextItem>
        </ListHeader>
        {active.map(g => (
          <div key={g.id} className="listContainer big groupPriceRow" style={{ gridTemplateColumns: COLUMNS }}>
            <div className="groupPriceName">
              <ParticipantGroupDot participantGroup={g} withTooltip={false} />
              <span className="groupPriceNameText">{g.name}</span>
            </div>
            <PriceCell value={g.defaultEntryPrice} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <main className="eventColumnMain groupPriceMain">
      <OfflineBanner />
      <div className="pricesSectionHeader">
        <div className="pricesSectionHeaderText">
          <h3 className="pricesSectionTitle">Eintritt je Gruppe</h3>
          <span className="pricesSectionHint">
            Standardpreise der Gruppen. Einzelne Spieltage können abweichen.
          </span>
        </div>
        {admin && (
          <ActionButton icon="tune" label="Gruppen bearbeiten" onClick={() => navigate(ROUTES.PARTICIPANT_GROUPS)} />
        )}
      </div>
      {content}
    </main>
  );
}

export default ParticipantGroupPriceTable;
